import { useEffect, useMemo, useState } from 'react';
import { getHqCatalog, previewHqRole, saveHqRole, checkHqRequest, HqCatalog, HqRolePreview } from '../../api/client';
import { cellFor, describeChange, diffKeys, modulesOf, toggle, PROTECTED_TEXT, WRITES_CLOSED_TEXT, VENDOR_KEYS } from './hqPermissionsModel';
import { refreshHqAccess } from './hqAccess';
import { PERMISSIONS_ROLE_KEY } from './RolesPanel';
import { PermissionGroupCard, GROUP_ROW, GROUP_LABEL_CELL, GROUP_ALLOWED_CELL, GROUP_STATUS_CELL } from './PermissionGroupCard';
import { getUnresolved, recordAttempt, markUnknown, clearAttempt, unresolvedKeys, classifyHqResponse, classifyHqCheck, NO_RECORD_YET_TEXT, lastRecordFailure, notStartedText } from './unresolvedChange';
import { useAttemptStore } from './useAttemptStore';
import { useVendorDirectory } from './useVendorDirectory';
import { personRetentionNote } from './vendorSources';

type RoleSaveBody = { roleId: string; keys: string[]; expectedVersion: number; previewToken: string };

/**
 * BUILD-039 — Settings › User Management › Permissions. ONE role at a time: a Role dropdown picks the role, and its saved permissions are shown as
 * a grouped Permission / Allowed checklist. Several boxes can be changed, then "Review changes" asks the server who would gain or lose what, and ONE Save
 * carries the version the editor started from AND the token of that review — a concurrent edit, or a change in who holds the role since the review, is
 * refused instead of applied unseen. A review that could not list everyone is never presented as complete and cannot be saved. Switching roles with unsaved
 * changes asks first. Viewers who cannot change access see the checklist read-only. Vendors can be given to any role, only by a Company Admin, with an
 * explicit confirmation. This edits the ROLE's defaults; one person's own additions/removals are changed from their account in the Directory.
 * Adding, renaming and retiring roles lives in the Roles tab (RolesPanel), not here.
 * The server decides every save.
 */
type Status = { kind: 'ok' | 'error' | 'unknown'; text: string };
type Draft = { roleId: string; keys: Set<string> };
const isVendor = (k: string) => (VENDOR_KEYS as readonly string[]).includes(k);

export function RolePermissionsMatrix({ nameOf }: { nameOf: (employeeId: string) => string }) {
  const [catalog, setCatalog] = useState<HqCatalog | null>(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState<string>('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [preview, setPreview] = useState<HqRolePreview | null>(null);
  const [vendorConfirmed, setVendorConfirmed] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  useAttemptStore();                                                     // re-render when the durable record of an unresolved attempt changes
  const { read: vendorRead } = useVendorDirectory(!!catalog?.viewer?.companyAdmin);   // named grants / Company Admins: other sources that may keep Vendors access when a role loses it
  const [askedRole] = useState<string | null>(() => { try { return sessionStorage.getItem(PERMISSIONS_ROLE_KEY); } catch { return null; } });

  async function load(keep?: string) {
    const r = await getHqCatalog().catch(() => null);
    if (!r) { setLoadError('Roles & permissions could not be loaded. Try again.'); return; }
    if (!r.ok) { setLoadError(r.status === 403 ? 'You do not have permission to see Unique HQ roles.' : (r.body?.message || 'Roles & permissions could not be loaded.')); return; }
    setLoadError(''); setCatalog(r.body);
    const asked = askedRole;
    const has = (id: string | null | undefined) => !!id && r.body.roles.some((x) => x.roleId === id);
    const open = unresolvedKeys('role:').map((k) => k.slice(5)).find((id) => has(id));      // a save whose answer was lost is shown first, not forgotten
    const want = has(keep) ? keep! : open ? open : has(asked) ? asked! : (r.body.roles.find((x) => !x.allPermissions) || r.body.roles[0])?.roleId || '';
    openRole(r.body, want);
  }
  useEffect(() => {
    load();
    // the role the Roles tab asked to open is used once (read when this view was created, so a double effect run cannot lose it)
    try { sessionStorage.removeItem(PERMISSIONS_ROLE_KEY); } catch { /* ignore */ }
  }, []);

  function openRole(cat: HqCatalog, roleId: string) {
    const role = cat.roles.find((x) => x.roleId === roleId);
    setSelected(roleId); setPreview(null); setVendorConfirmed(false); setRequestId(crypto.randomUUID());
    setDraft(role ? { roleId: role.roleId, keys: new Set(role.permissions) } : null);
  }

  const labelOf = useMemo(() => {
    const m = new Map((catalog?.permissions || []).map((p) => [p.key, p.label]));
    return (k: string) => m.get(k) || k;
  }, [catalog]);

  if (loadError) return <div className="p-4 text-sm text-red-700" role="alert">{loadError} <button className="underline" onClick={() => load()}>Retry</button></div>;
  if (!catalog || !draft) return <div className="p-4 text-sm text-gray-500" role="status">Loading roles…</div>;

  const unresolvedKey = `role:${selected}`;
  const unresolved = getUnresolved<RoleSaveBody>(unresolvedKey);     // an attempt whose answer was lost: nothing else may be started for this role
  const viewer = catalog.viewer;
  const writesClosed = catalog.writes?.open === false;               // the platform path is not open for permission writes yet: read-only, truthfully
  const canChange = viewer.canChange !== false && !writesClosed;
  const role = catalog.roles.find((r) => r.roleId === selected);
  if (!role) return <div className="p-4 text-sm text-gray-500" role="status">That role is no longer available. <button className="underline" onClick={() => load()}>Reload</button></div>;
  const roleLock = writesClosed ? WRITES_CLOSED_TEXT : !canChange ? PROTECTED_TEXT.readOnlyViewer : role.allPermissions ? PROTECTED_TEXT.ownerAll : role.privileged && !viewer.platformOwner ? PROTECTED_TEXT.adminNeedsPlatformOwner : null;
  const diff = diffKeys(role.permissions, draft.keys);
  const dirty = diff.added.length > 0 || diff.removed.length > 0;
  const vendorChange = [...diff.added, ...diff.removed].some(isVendor);
  const saveBlocked = !!preview && (!preview.complete || (vendorChange && !vendorConfirmed));
  const statusClass = status?.kind === 'ok' ? 'bg-green-50 text-green-700' : status?.kind === 'unknown' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700';
  const q = search.trim().toLowerCase();
  const modules = modulesOf(catalog.permissions)
    .map((m) => ({ ...m, permissions: m.permissions.filter((p) => !q || p.label.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || m.module.toLowerCase().includes(q)) }))
    .filter((m) => m.permissions.length);

  /** true when it is fine to leave the current draft (nothing unsaved, or the person agreed to discard it) */
  function okToLeave(): boolean {
    return !dirty || window.confirm(`Discard your unsaved changes to "${role!.label}"?`);
  }
  function pick(id: string) {
    if (id === selected || !okToLeave()) return;
    setStatus(null);
    openRole(catalog!, id);
  }
  function discard() {
    if (unresolved) return;
    setStatus(null);
    openRole(catalog!, selected);
  }
  function flip(key: string) { if (unresolved) return; setPreview(null); setDraft({ ...draft!, keys: toggle(draft!.keys, key) }); }

  async function review() {
    if (unresolved) return;                                              // a new review must never replace an unresolved attempt
    setBusy(true); setStatus(null); setVendorConfirmed(false);
    const r = await previewHqRole(selected, [...draft!.keys]).catch(() => null);
    setBusy(false);
    if (!r || !r.ok) { setStatus({ kind: 'error', text: r?.body?.message || 'The change could not be reviewed.' }); return; }
    setPreview(r.body);
    setRequestId(crypto.randomUUID());                                  // a new review is a new change: it gets a new request id
  }
  /** Save the reviewed change; or, when an attempt is unresolved, send THAT attempt again (same request id, same payload, never the current draft). */
  async function save() {
    const again = unresolved;
    if (!preview && !again) return;
    const rid = again ? again.requestId : requestId;
    const body: RoleSaveBody = again ? again.payload : { roleId: selected, keys: [...draft!.keys], expectedVersion: role!.version, previewToken: preview!.previewToken };
    // The request id + payload are written to durable browser storage BEFORE anything is sent; if that cannot be done (no known actor) nothing is sent.
    if (!recordAttempt(unresolvedKey, { requestId: rid, payload: body, label: `the role "${role!.label}"` })) { setStatus({ kind: 'error', text: notStartedText(lastRecordFailure()) }); return; }
    setBusy(true); setStatus(null);
    const r = await saveHqRole(rid, body).catch(() => null);
    setBusy(false);
    // Only a recorded outcome, a first send's own definite refusal, or a refusal the platform recorded (fenced) settles the attempt (see classifyHqResponse). Any other answer
    // (a generic 500, a 502 / 504 / 429, an ordinary refusal of a retry, a malformed body, a lost answer) leaves it unresolved with the same request id and payload.
    if (!r || classifyHqResponse(r, !!again) === 'unresolved') {      // `again` = an earlier copy of this request may exist (also after a reload)
      markUnknown(unresolvedKey, rid);
      setStatus({ kind: 'unknown', text: 'It is not known whether the role was saved. It stays unresolved: use "Check", or "Save again (same change)", which is applied once.' });
      return;
    }
    clearAttempt(unresolvedKey, rid);                                     // a validated recorded outcome or a proven refusal settles the attempt
    if (!r.ok) {
      const reason = r.body?.reason;
      if (reason === 'version-conflict') { setStatus({ kind: 'error', text: 'Someone else changed this role since you opened it. Your edit was not saved; the latest version is shown now.' }); await load(selected); return; }
      if (reason === 'preview-stale') { setPreview(null); setStatus({ kind: 'error', text: 'Who this role affects has changed since your review. Nothing was saved. Review the changes again.' }); return; }
      setStatus({ kind: 'error', text: r.body?.message || 'The change was refused. Nothing was saved.' });
      return;
    }
    setStatus({ kind: 'ok', text: r.body.replayed ? 'This change was already saved.' : `Saved "${role!.label}". People with this role get the new permissions within about a minute.` });
    refreshHqAccess(); await load(selected);
  }
  async function check() {
    if (!unresolved) return;
    const r = await checkHqRequest(unresolved.requestId).catch(() => null);
    if (!r || !r.ok) { setStatus({ kind: 'unknown', text: 'Still not known. Try Check again in a moment.' }); return; }
    const v = classifyHqCheck(r);
    if (v === 'applied') { clearAttempt(unresolvedKey, unresolved.requestId); setStatus({ kind: 'ok', text: 'Checked: the role change WAS saved.' }); refreshHqAccess(); await load(selected); }
    else if (v === 'refused') { clearAttempt(unresolvedKey, unresolved.requestId); setStatus({ kind: 'error', text: 'Checked: the platform refused this change and recorded the refusal, so it can never be applied. Nothing was saved.' }); await load(selected); }
    else setStatus({ kind: 'unknown', text: NO_RECORD_YET_TEXT });         // absent record = still unknown (the first attempt may yet commit), never "not saved"
  }
  const editable = !roleLock;
  return (
    <div className="p-4 max-w-4xl" data-testid="role-permissions-matrix">
      <p className="text-xs text-gray-600 mb-3">
        What a Unique HQ role can do by default. Changes apply to everyone who holds the role within about a minute, except where a person has their own
        addition or removal (changed from their account in the Directory). Pricing and Project Manager roles are not affected. Roles are added, renamed and
        retired in the Roles tab.
      </p>
      <p className="text-xs text-gray-600 mb-3" data-testid="standard-access-note">
        These boxes are the extra abilities a role adds, mostly managing or administering things. An unticked box does not take away the everyday use of
        Unique HQ that everyone with access already has; for example, "Manage the library" adds editing the library, and leaving it unticked does not stop
        anyone from reading it.
      </p>
      {writesClosed && <p className="mb-3 text-xs text-amber-800 bg-amber-50 rounded px-3 py-2" role="status" data-testid="writes-closed">{WRITES_CLOSED_TEXT}</p>}
      {!canChange && !writesClosed && <p className="mb-3 text-xs text-gray-600" data-testid="matrix-read-only">{PROTECTED_TEXT.readOnlyViewer}</p>}

      {/* stays in view while the checklist scrolls: the role being edited and the change actions */}
      <div className="sticky top-0 z-10 bg-white border-b pb-3 pt-1" data-testid="role-toolbar">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">Role
            <select aria-label="Role" className="block mt-1 border rounded px-2 py-1 text-sm min-w-[14rem]" value={selected} disabled={busy} onChange={(e) => pick(e.target.value)} data-testid="role-select">
              {catalog.roles.map((r) => <option key={r.roleId} value={r.roleId}>{r.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-600">Find a permission
            <input aria-label="Find a permission" value={search} onChange={(e) => setSearch(e.target.value)} className="block mt-1 border rounded px-2 py-1 text-sm w-52" />
          </label>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-600" data-testid="draft-diff">
              {dirty ? `${diff.added.length + diff.removed.length} unsaved change${diff.added.length + diff.removed.length === 1 ? '' : 's'}` : 'No unsaved changes'}
            </span>
            {editable && !unresolved && (!preview ? (
              <button disabled={busy || !dirty} onClick={review} className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium disabled:opacity-50" data-testid="review-changes">Review changes</button>
            ) : (
              <button disabled={busy || saveBlocked} onClick={save} className="px-3 py-1.5 bg-blue text-white rounded text-xs font-medium disabled:opacity-50" data-testid="confirm-save">Save role</button>
            ))}
            {editable && dirty && !unresolved && <button disabled={busy} onClick={discard} className="px-3 py-1.5 border rounded text-xs" data-testid="discard-changes">Cancel</button>}
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-600" data-testid="role-description">{role.description}</p>
        {roleLock && canChange && <p className="mt-1 text-xs text-gray-700" data-testid="role-lock">🔒 {roleLock}</p>}

        {status && (
          <div role={status.kind === 'ok' ? 'status' : 'alert'} className={`mt-2 text-sm rounded px-3 py-2 ${statusClass}`} data-testid="matrix-status">
            {status.text}
          </div>
        )}
        {unresolved && (
          <div role="alert" className="mt-2 text-sm rounded px-3 py-2 bg-amber-50 text-amber-800" data-testid="matrix-unresolved">
            An earlier save of {unresolved.label} has an unknown result. Nothing else can be changed for this role until it is settled.
            <button className="ml-2 underline" disabled={busy} onClick={check} data-testid="matrix-check">Check</button>
            <button className="ml-2 underline" disabled={busy} onClick={save} data-testid="matrix-save-again">Save again (same change)</button>
          </div>
        )}
        {preview && (
          <div className="mt-2 p-3 border rounded bg-gray-50 text-sm max-h-60 overflow-y-auto" data-testid="role-preview">
            <ul className="list-disc pl-5 text-gray-700 mb-1">{describeChange(diff, labelOf).map((l) => <li key={l}>{l}</li>)}</ul>
            <p className="font-medium">Who this affects</p>
            {!preview.complete && (
              <p className="text-red-700" role="alert" data-testid="role-preview-incomplete">Not everyone who holds this role could be listed, so this change cannot be saved now. Try again later.</p>
            )}
            {preview.people.length === 0 ? <p className="text-gray-600">{preview.complete ? 'Nobody with a person record holds this role right now.' : 'Nobody could be listed.'}</p> : (
              <ul className="list-disc pl-5 text-gray-700">
                {preview.people.map((p) => {
                  const keep = personRetentionNote(p.removed, vendorRead, p.employeeId);
                  return <li key={p.employeeId}>{nameOf(p.employeeId)}: {describeChange(p, labelOf).join('; ')}{keep && <span className="block text-xs text-gray-600" data-testid={`role-vendor-retention-${p.employeeId}`}>{keep}</span>}</li>;
                })}
              </ul>
            )}
            {preview.unmatchedLogins > 0 && (
              <p className="text-xs text-gray-600" data-testid="role-preview-unmatched">{preview.unmatchedLogins} sign-in{preview.unmatchedLogins === 1 ? '' : 's'} with this role {preview.unmatchedLogins === 1 ? 'has' : 'have'} no person record and {preview.unmatchedLogins === 1 ? 'is' : 'are'} not listed.</p>
            )}
            {vendorChange && preview.complete && (
              <label className="mt-2 flex items-start gap-2 text-xs text-gray-800">
                <input type="checkbox" checked={vendorConfirmed} onChange={(e) => setVendorConfirmed(e.target.checked)} data-testid="confirm-vendors" />
                <span>I confirm this changes Vendors access for everyone who holds this role.</span>
              </label>
            )}
          </div>
        )}
      </div>

      {/* one bordered card per category, so each group of permissions is clearly separated */}
      <div className="mt-3 space-y-4" data-testid="role-checklist">
          {modules.map((m) => (
            <PermissionGroupCard key={m.module} title={m.module}>
              {m.permissions.map((p) => {
                const cell = cellFor(role, p, viewer, draft.keys);
                const changed = draft.keys.has(p.key) !== role.permissions.includes(p.key);
                const locked = cell.kind !== 'editable';
                const reason = cell.kind === 'editable' ? null : cell.reason;
                return (
                  <tr key={p.key} className={GROUP_ROW} data-testid={`role-row-${p.key}`}>
                    <th scope="row" className={GROUP_LABEL_CELL}>
                      <div className="text-gray-800">{p.label}</div>
                      <div className="text-[11px] text-gray-500">{p.description}</div>
                    </th>
                    <td className={GROUP_ALLOWED_CELL}>
                      <input type="checkbox" checked={cell.on} disabled={locked || busy || !!unresolved} onChange={() => flip(p.key)} aria-label={`${p.label}: allowed for ${role.label}`}
                        title={reason || undefined} className="rounded border-gray-300 disabled:opacity-60" data-testid={`cell-${p.key}`} />
                    </td>
                    <td className={GROUP_STATUS_CELL}>
                      {changed ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium" data-testid={`changed-${p.key}`}>Changed</span>
                        : cell.kind === 'protected' || (reason && reason !== roleLock) ? <span className="text-gray-500" title={reason || undefined}>🔒 {shortLock(reason || '')}</span> : null}
                    </td>
                  </tr>
                );
              })}
            </PermissionGroupCard>
          ))}
      </div>
      {modules.length === 0 && <p className="mt-3 text-xs text-gray-500">No permission matches "{search}".</p>}
      <p className="mt-3 text-xs text-gray-500">
        Company Admin and Platform Owner are company-level authority, not role permissions: they are shown on each person and changed only through the protected
        account process. HR access is given in HR Administration › Access.
      </p>
    </div>
  );
}

function shortLock(reason: string): string {
  if (/Owner only/.test(reason)) return 'Owner only';
  if (/Owner role always/.test(reason)) return 'Always (Owner)';
  if (/Company Admin can change Vendors/.test(reason)) return 'Company Admin decides';
  if (/Platform Owner/.test(reason)) return 'Platform Owner decides';
  return 'Locked';
}
